import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCompanies } from "../../../api/company";
import {
  CreateAccountFormData,
  useCreateAccount,
} from "../../../api/registration";
import { ENUMUSERTYPE_OPTION } from "../../../generated/enums";
import { Company } from "../../../generated/types";
import {
  VALIDATION_RULES,
  validateForm as validateFormUtil,
} from "../../../utils/formValidation";
import { Button } from "../../common/Button";
import { CityField } from "../../common/CityField";
import { FormCheckbox, FormInput, FormSelect } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";
import { sectionToIcon } from "../../type/default";

type CreateAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ConfirmationModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirmation"
      icon="check-circle"
      maxWidth="sm"
    >
      <div className="text-center py-4">
        <div className="mb-4 text-primary">
          <FontAwesomeIcon icon="check-circle" size="3x" />
        </div>
        <p className="text-lg mb-6">
          L'utilisateur a bien été créé, il recevra un mail de vérification.
        </p>
        <Button onClick={onClose} className="w-full">
          Fermer
        </Button>
      </div>
    </Modal>
  );
};

const getValidationRules = (formData: any) => {
  const baseRules = {
    nom: [
      VALIDATION_RULES.required(),
      VALIDATION_RULES.minLength(2),
      VALIDATION_RULES.onlyLetters(),
    ],
    prenom: [
      VALIDATION_RULES.required(),
      VALIDATION_RULES.minLength(2),
      VALIDATION_RULES.onlyLetters(),
    ],
    email: [VALIDATION_RULES.required(), VALIDATION_RULES.email()],
    /*emailVerification: [
      VALIDATION_RULES.required(),
      VALIDATION_RULES.match(
        "email",
        formData,
        "Les adresses email ne correspondent pas"
      ),
    ],*/
  };

  // Règles pour la création d'une nouvelle entreprise
  const companyRules = formData.createNewCompany
    ? {
        company_name: [
          VALIDATION_RULES.required(),
          VALIDATION_RULES.minLength(2),
        ],
        siret: [VALIDATION_RULES.required(), VALIDATION_RULES.siret()],
        address: [VALIDATION_RULES.required(), VALIDATION_RULES.minLength(5)],
        zip_code: [VALIDATION_RULES.required(), VALIDATION_RULES.postalCode()],
        city: [VALIDATION_RULES.required()],
        phone_mobile: [VALIDATION_RULES.required(), VALIDATION_RULES.phone()],
        companyEmail: [VALIDATION_RULES.required(), VALIDATION_RULES.email()],
      }
    : {
        company: [
          VALIDATION_RULES.required("Veuillez sélectionner une entreprise"),
        ],
      };

  // Règles spécifiques pour les installateurs
  const installerRules =
    formData.type === 3
      ? {
          id_x3: [
            VALIDATION_RULES.required(
              "Le code X3 est requis pour les installateurs"
            ),
          ],
        }
      : {};

  return {
    ...baseRules,
    ...companyRules,
    ...installerRules,
  };
};

export const CreateAccountModal = ({
  isOpen,
  onClose,
}: CreateAccountModalProps) => {
  const { t } = useTranslation();
  const { createAccount } = useCreateAccount();
  const [formData, setFormData] = useState<CreateAccountFormData>({
    type: "breeder",
    language: "fr_FR",
    nom: "",
    prenom: "",
    email: "",
    id_x3: 0,
    company: -1,
    createNewCompany: false,
    company_name: "",
    siret: "",
    address: "",
    address_complementary: "",
    zip_code: "",
    city: "",
    country: "FR",
    phone_mobile: "",
    phone_fixe: "",
    companyEmail: "",
  });

  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const COMPANIES_OPTION: {
    value: number;
    label: string;
  }[] = companies.map((company) => ({
    value: company.id,
    label: `${company.company_name} (${company.siret})`,
  }));

  useEffect(() => {
    const fetchCompanys = async () => {
      if (isOpen) {
        setIsLoading(true);

        const companiesResult = await fetchCompanies();

        if (companiesResult.error) {
          console.error(
            t("error-company-list-fetch-failed"),
            companiesResult.error
          );

          setIsLoading(false);
          return;
        } else {
          setCompanies(companiesResult.Companys);

          setIsLoading(false);
        }
      }
    };

    fetchCompanys();
  }, [isOpen, t]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type: inputType } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        inputType === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : name === "company" || name === "type"
            ? parseInt(value)
            : value,
      // Changer de code postal invalide la ville choisie pour l'ancien.
      ...(name === "zip_code" && { city: "" }),
    }));

    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCityChange = (city: string, zipCode: string) => {
    setFormData((prev) => ({ ...prev, city, zip_code: zipCode }));
    setErrors((prev) => {
      const { city: _city, zip_code: _zip, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Vérifier si une entreprise est sélectionnée
    if (formData.company === -1 && !formData.createNewCompany) {
      setError(t("error-company-select-required"));
      return;
    }

    const validationRules = getValidationRules(formData);
    const validationErrors = validateFormUtil(formData, validationRules);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await createAccount(formData);

      if (response.success) {
        setShowConfirmation(true);
      } else {
        setError(
          (response.error && t(response.error)) ||
            t("error-account-create-failed")
        );
      }
    } catch (err) {
      console.error("Erreur:", err);
      setError(t("error-account-create-failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
    // Réinitialiser le formulaire
    setFormData({
      type: "breeder",
      language: "fr_FR",
      nom: "",
      prenom: "",
      email: "",
      id_x3: 0,
      company: -1,
      createNewCompany: false,
      company_name: "",
      siret: "",
      address: "",
      address_complementary: "",
      zip_code: "",
      city: "",
      country: "FR",
      phone_mobile: "",
      phone_fixe: "",
      companyEmail: "",
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Créer un compte"
        icon={sectionToIcon.section_account}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <FormSelect
            label="Type de compte"
            name="type"
            value={formData.type}
            onChange={handleChange}
            options={ENUMUSERTYPE_OPTION}
            required
          />

          <FormCheckbox
            label="Créer une nouvelle entreprise"
            name="createNewCompany"
            checked={formData.createNewCompany}
            onChange={handleChange}
          />

          {!formData.createNewCompany ? (
            <FormSelect
              label="Entreprise existante"
              name="company"
              value={formData.company}
              onChange={handleChange}
              options={COMPANIES_OPTION}
              required={!formData.createNewCompany}
              disabled={isLoading}
              error={errors.company}
            />
          ) : (
            <div className="space-y-4 border-l-2 border-secondary pl-4">
              <FormInput
                label="Raison sociale"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required={formData.createNewCompany}
                error={errors.company_name}
              />

              <FormInput
                label="SIRET"
                name="siret"
                value={formData.siret}
                onChange={handleChange}
                required={formData.createNewCompany}
                error={errors.siret}
              />

              <FormInput
                label="Adresse"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required={formData.createNewCompany}
                error={errors.address}
              />

              <FormInput
                label="Complément d'adresse"
                name="address_complementary"
                value={formData.address_complementary}
                onChange={handleChange}
              />

              <div className="flex gap-4">
                <FormInput
                  label="Code postal"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  required={formData.createNewCompany}
                  error={errors.zip_code}
                />

                <CityField
                  zipCode={formData.zip_code}
                  value={formData.city}
                  onChange={handleCityChange}
                  required={formData.createNewCompany}
                  error={errors.city}
                />
              </div>

              <FormInput
                label="Téléphone mobile"
                name="phone_mobile"
                value={formData.phone_mobile}
                onChange={handleChange}
                required={formData.createNewCompany}
                error={errors.phone_mobile}
              />

              <FormInput
                label="Téléphone fixe"
                name="phone_fixe"
                value={formData.phone_fixe}
                onChange={handleChange} //requuired si pas de mobile ?
                error={errors.phone}
              />

              <FormInput
                label="Email"
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleChange}
                required={formData.createNewCompany}
                error={errors.companyEmail}
              />
            </div>
          )}

          <div className="border-t border-grey-light pt-4">
            <h3 className="font-oxaniumMedium mb-4">
              Informations personnelles
            </h3>
            <div className="space-y-4">
              <FormInput
                label="Nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                error={errors.nom}
              />

              <FormInput
                label="Prénom"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                required
                error={errors.prenom}
              />

              <FormInput
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={errors.email}
              />

              {(formData.type === "installer" ||
                formData.type === "installer_assistant") && (
                <FormInput
                  label="Code X3"
                  name="id_x3"
                  value={formData.id_x3}
                  onChange={handleChange}
                  required
                  error={errors.id_x3}
                />
              )}
            </div>
          </div>

          <ModalActions>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading ||
                (formData.company === -1 && !formData.createNewCompany)
              }
            >
              {isLoading ? "Création en cours..." : "Créer"}
            </Button>
          </ModalActions>
        </form>
      </Modal>

      <ConfirmationModal isOpen={showConfirmation} onClose={handleClose} />
    </>
  );
};
